import { Depths, ScrollablePane } from "@fluentui/react";
import { Spinner } from "@fluentui/react-components";
import * as React from "react";
import ReactHtmlParser from "react-html-parser";
import { useParams } from "react-router";
import { IServiceDataType, PAGE } from "../App";
import { useAppInsights } from "../components/AppInsights/AppInsights";
import { buildPageViewTelemetry, trackPageView } from "../components/AppInsights/LoggingHelper";
import { TimeZone } from "../components/Layout/Header";
import { DetailsListJobHistory, IJobHistoryProps } from "../components/JobHistory/DetailsListJobHistory";
import { AdminMessageBanner } from "../components/Home/AdminMessageBanner";
import { BackToTopButton } from "../components/Shared/BackToTopButton";
import { PagePermissionsComponent } from "../components/Shared/PagePermissionsComponent";
import { RootContext } from "../components/Stores/RootStore";
import { UserPermission } from "../services/AuthenticationService/AuthReducer";
import { DataActionEnum } from "../services/DataContext/DataActions";
import HttpService from "../services/HttpService/HttpService";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { breakWord, _Styles } from "../Styles/Page.styles";
import { IPageProps } from "../Styles/Page.types";

const pageTitle: string = "Job History";
const errorText: string = "You must be an administrator to view this page.";

export interface IJobHistoryPageState {
    refreshData: number;
    timezone: TimeZone | undefined;
    selectedService: IServiceDataType | undefined;
    appInsights: ApplicationInsights | undefined;
}

export const JobHistoryPage: React.FunctionComponent<IPageProps> = (props: IPageProps) => {
    const { state, dispatch } = React.useContext(RootContext);
    const [refreshData] = React.useState<number>(0);

    // read "serviceTreeGuid" from url param using useParams() hook
    const { serviceTreeGuid: serviceTreeGuidFromParam } = useParams();
    const [selectedService] = React.useState<IServiceDataType>({
        ServiceName: "",
        ServiceTreeGUID: serviceTreeGuidFromParam && serviceTreeGuidFromParam.length > 0 ? serviceTreeGuidFromParam : "",
    });

    const appInsights = useAppInsights();

    // HttpService — created up front to mirror the pattern used by sibling pages
    const [httpService] = React.useState(HttpService(appInsights, state));

    React.useEffect(() => {
        document.title = pageTitle;

        dispatch({
            type: DataActionEnum.UPDATE_CURRENT_PAGE,
            currentPage: "jobhistory",
        });

        const telemetry = buildPageViewTelemetry(
            pageTitle,
            "/jobhistory",
            "/jobhistory",
            state.AuthStore.Email,
            state.AuthStore.isAdmin,
        );

        trackPageView(appInsights, telemetry);
    }, []);

    const jobHistoryProps: IJobHistoryProps = {
        refreshData,
        timezone: state.AuthStore.timezone,
        selectedService,
        appInsights,
    };

    return (
        <ScrollablePane className={props.isNavCollapsed ? _Styles.scrollablePaneCollapsed : _Styles.scrollablePaneExpand}>
            {state.AuthStore.isAdmin ? (
                <>
                    <div style={{ boxShadow: Depths.depth8, position: "relative", display: "block", margin: "0px" }}>
                        <AdminMessageBanner page={PAGE.JOBHISTORY} />
                    </div>
                    <div className={_Styles.rootDiv}>
                        <div hidden={state.AuthStore.UserPermitted !== UserPermission.Permitted || state.AuthStore.UserTimeOut}>
                            <h1 id="jobhistorytitle" className={breakWord}>
                                {ReactHtmlParser(pageTitle)}
                            </h1>
                            {state.AuthStore.Token !== "" ? (
                                <DetailsListJobHistory {...jobHistoryProps} />
                            ) : (
                                <div
                                    style={{
                                        position: "absolute", left: "50%", top: "40%",
                                        transform: "translate(-50%, -50%)",
                                    }}>
                                    <Spinner size="large" aria-live="assertive" />
                                </div>
                            )}
                        </div>
                        <BackToTopButton />
                        <PagePermissionsComponent />
                    </div>
                </>
            ) : (
                <div className={_Styles.rootDiv}>
                    <h1 id="jobhistorytitle" className={breakWord}>
                        {ReactHtmlParser(pageTitle)}
                    </h1>
                    <p>{errorText}</p>
                    <BackToTopButton />
                    <PagePermissionsComponent />
                </div>
            )}
        </ScrollablePane>
    );
};
